import { Grid3X3, MapPin, Camera, Zap } from "lucide-react";
import { JointIcon, ExplosionIcon, EllipsoidIcon } from "../ui/Icons";

// ─── layout constants ─────────────────────────────────────────────────────────
export const SATELLITE_Y_GAP = 28; // px between satellite nodes vertically
export const BASE_LEVEL_GAP = 90; // minimum clear gap between body-level satellite extents
export const MIN_LEVEL_SPACING = 130; // absolute floor so body circles + joint node never crowd
export const CHILD_SPACING = 50;
export const ROOT_SPACING = 80;
export const GLOBAL_SPACING = 110;
export const FLAT_GRID_X_GAP = 42;
export const FLAT_GRID_Y_GAP = 56;
export const FIXED_JOINT_GROUND_STEM_PX = 58;
export const FIXED_JOINT_GROUND_HALF_WIDTH_PX = 17;
export const ROOT_OBJECT_NODE_Y_OFFSET = FIXED_JOINT_GROUND_STEM_PX + 40;
export const SCENE_GRAPH_TOP_PADDING = ROOT_OBJECT_NODE_Y_OFFSET + 50;

// Joint-linked actuator positioning — hang snugly below the joint node
export const ACT_INDENT = 20; // px right of joint centre
export const ACT_Y_START = 24; // px below joint centre for first actuator
export const ACT_Y_GAP = 20; // px between stacked actuators

// Invisible stroke width on tendon / equality curves so clicks are easy to land.
export const SCENE_GRAPH_EDITOR_EDGE_HIT_STROKE = 14;
// Extra bow (px) per lane when multiple tendon/equality curves share the same endpoints.
export const SCENE_GRAPH_PARALLEL_ARC_BOW_STEP = 22;
export const TRACKPAD_WHEEL_DELTA_MAX_PX = 80;
export const TRACKPAD_WHEEL_SESSION_MS = 220;
export const SCENE_GRAPH_MODIFIED_DRAG_PAN_MULTIPLIER = 3;
export const OBJECT_MOVEMENT_TOOLTIP_LABEL = "Fixed in Space";

// Actuated tendon curves: alternating cyan / amber dashes with real transparent gaps.
// Each stroke uses `dash, (gap + otherDash + gap)`; amber stroke is phased by (dash+gap).
export const SCENE_GRAPH_TENDON_ACTUATED_DASH_PX = 6;
export const SCENE_GRAPH_TENDON_ACTUATED_GAP_PX = 4;

// Swatches for legend rows that mirror graph edge strokes (see getEdgeProps).
export const LEGEND_CURVE_TENDON = { stroke: "#38bdf8", dasharray: "4 3" };
export const LEGEND_CURVE_EQUALITY = { stroke: "#a78bfa", dasharray: "5 3" };

/** Matches `getEdgeProps` actuator-joint / actuator-tendon — legend curve behind actuator disk. */
export const LEGEND_CURVE_ACTUATOR_EDGE = { stroke: "#fbbf24", dasharray: "5 3" };

// Types the user can toggle from the legend. Body/object are rendered as fixed rows above these.
export const LEGEND_ITEMS = [
  { type: "joint", label: "Joint", Icon: JointIcon, iconCls: "w-3 h-3" },
  { type: "mesh", label: "Visual", Icon: Grid3X3, iconCls: "w-2 h-2" },
  { type: "geom", label: "Collision", Icon: ExplosionIcon, iconCls: "w-2 h-2" },
  { type: "site", label: "Site", Icon: MapPin, iconCls: "w-2 h-2" },
  { type: "inertial", label: "Inertial", Icon: EllipsoidIcon, iconCls: "w-2 h-2" },
  { type: "camera", label: "Camera", Icon: Camera, iconCls: "w-2 h-2" },
  { type: "actuator", label: "Actuator", Icon: Zap, iconCls: "w-2 h-2" },
  { type: "tendon", label: "Tendon", curve: LEGEND_CURVE_TENDON },
  { type: "equality", label: "Constraint", curve: LEGEND_CURVE_EQUALITY },
];
export const LEGEND_HELP_TEXT = "Click a legend item to hide or show that type in the graph.";

/** Disk diameter and curve swatch width — kept equal so legend rows align. */
export const LEGEND_SWATCH_SIZE_PX = 20;
/** Curve swatch height (slightly shorter than disk so the arc stays compact). */
export const LEGEND_CURVE_VIEW_H_PX = 12;

/** Quadratic path shared by tendon legend swatch and actuator backing (same geometry). */
export const LEGEND_SWATCH_CURVE_QUADRATIC_D = `M 1 ${LEGEND_CURVE_VIEW_H_PX - 1.5} Q ${LEGEND_SWATCH_SIZE_PX / 2} 1.5 ${LEGEND_SWATCH_SIZE_PX - 1} ${LEGEND_CURVE_VIEW_H_PX - 1.5}`;

// When a parent type is hidden its dependents are forced hidden too.
// The user's hiddenTypes stores their explicit choices; effectiveHiddenTypes
// is the derived set used for all layout and rendering.
export const FORCED_HIDDEN_WHEN = {
  joint: ["actuator"],
};

export const SELECTABLE_TYPES = new Set([
  "body",
  "joint",
  "site",
  "attachment_site",
  "geom",
  "inertial",
  "mesh",
  "actuator",
  "camera",
]);
export const EDITOR_OPEN_TYPES = new Set(["tendon", "equality"]);
export const DEFAULT_ZOOM = 1.05;
